import { Search } from "lucide-react";
import React, { FormEvent, forwardRef, useCallback, useState } from "react";
import { cn } from "../../../../utils/cn";

const SEARCH_BAR_FONT = "font-['Montserrat',sans-serif]";
const SUBMIT_BUTTON_BG = "bg-[#6FA1F7]";
const SUBMIT_BUTTON_BG_HOVER = "hover:bg-[#5E96F0]";

export type StyleGuideSearchBarToHeaderProps = {
  formClassName?: string;
  isToShowSearchBarMobile?: boolean;
  hasError?: boolean;
  submitLoading?: boolean;
  resultsCount?: number;
  onSubmitSearch?: (query: string) => void | Promise<void>;
  submitLabel?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type">;

const StyleGuideSearchBarToHeader = forwardRef<
  HTMLInputElement,
  StyleGuideSearchBarToHeaderProps
>(function StyleGuideSearchBarToHeader(
  {
    formClassName,
    isToShowSearchBarMobile = true,
    hasError = false,
    submitLoading = false,
    resultsCount,
    onSubmitSearch,
    submitLabel = "PESQUISAR",
    disabled,
    placeholder = "Pesquisar imóveis",
    className,
    defaultValue,
    value,
    onChange,
    ...rest
  },
  ref
) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(
    () => (typeof defaultValue === "string" ? defaultValue : "")
  );

  const currentValue = isControlled ? String(value ?? "") : internalValue;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      if (!isControlled) {
        setInternalValue(event.target.value);
      }
    },
    [isControlled, onChange]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!onSubmitSearch || submitLoading) {
        return;
      }
      await onSubmitSearch(currentValue.trim());
    },
    [currentValue, onSubmitSearch, submitLoading]
  );

  const showCounter =
    typeof resultsCount === "number" && resultsCount > 0 && !hasError;

  return (
    <form
      role="search"
      onSubmit={handleSubmit}
      className={cn(
        "relative flex w-full max-w-full min-w-0 flex-col items-stretch justify-center",
        "max-md:m-0",
        "md:ml-3",
        isToShowSearchBarMobile && "max-md:flex max-md:flex-col",
        "min-[400px]:max-md:flex min-[400px]:max-md:flex-col",
        formClassName,
        isToShowSearchBarMobile === false && "max-md:hidden"
      )}
    >
      <div
        className={cn(
          "flex w-full min-w-0 max-w-md flex-row items-stretch rounded-lg border border-solid shadow-none",
          "min-h-[40px] h-10",
          SEARCH_BAR_FONT,
          hasError ? "border-[#FF3838]" : "border-[#D1D5DB]"
        )}
      >
        <div className="flex min-h-0 min-w-0 flex-1 items-center rounded-l-lg bg-white pl-3 pr-2">
          <Search
            className="h-[18px] w-[18px] shrink-0 text-[#9CA3AF]"
            aria-hidden
          />
          <input
            ref={ref}
            type="search"
            disabled={disabled || submitLoading}
            placeholder={placeholder}
            autoComplete="off"
            value={isControlled ? value : internalValue}
            onChange={handleChange}
            className={cn(
              "ml-2 min-h-0 min-w-0 flex-1 border-0 bg-transparent text-sm font-normal leading-tight text-[#374151] outline-none ring-0",
              "placeholder:font-normal placeholder:text-[#9CA3AF]",
              hasError ? "placeholder:text-[#FF3838]" : undefined,
              className
            )}
            {...rest}
          />
        </div>
        <button
          type="submit"
          disabled={disabled || submitLoading}
          className={cn(
            "shrink-0 rounded-r-lg border-0 px-4 text-sm font-bold uppercase tracking-wide text-white outline-none transition-colors",
            "min-w-[7.5rem] max-w-[40%] sm:min-w-[8.5rem]",
            SUBMIT_BUTTON_BG,
            SUBMIT_BUTTON_BG_HOVER,
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4A7FD4]",
            "disabled:cursor-not-allowed disabled:opacity-60"
          )}
        >
          {submitLoading ? "…" : submitLabel}
        </button>
      </div>
      {showCounter && (
        <p className="mt-1 text-xs font-medium text-[#666666]" aria-live="polite">
          {resultsCount} result{resultsCount === 1 ? "" : "s"}
        </p>
      )}
    </form>
  );
});

export default StyleGuideSearchBarToHeader;
